/**
 * Theme Transition Utility
 *
 * PI5: Provides smooth transitions when switching themes to prevent page flash.
 * PI7: Emits transition events for loading indicator integration
 *
 * Uses View Transition API when available, falls back to opacity transition.
 * Psychology: Smooth transitions feel more premium and less jarring
 */

// PI7: Global transition state tracking
let isTransitioning = false
const transitionListeners = new Set<(transitioning: boolean) => void>()

/**
 * PI7: Subscribe to theme transition state changes
 */
export function onTransitionChange(listener: (transitioning: boolean) => void): () => void {
  transitionListeners.add(listener)
  // Return unsubscribe function
  return () => transitionListeners.delete(listener)
}

/**
 * PI7: Get current transition state
 */
export function getIsTransitioning(): boolean {
  return isTransitioning
}

/**
 * PI7: Notify all listeners of transition state change
 */
function notifyTransitionChange(transitioning: boolean) {
  isTransitioning = transitioning
  transitionListeners.forEach((listener) => listener(transitioning))
}

/**
 * Check if View Transition API is available
 */
function supportsViewTransitions(): boolean {
  return typeof document !== 'undefined' && 'startViewTransition' in document
}

/**
 * Execute a theme change with smooth transition
 *
 * @param callback - Function to execute during transition
 * @returns Promise that resolves when transition is complete
 *
 * @example
 * ```typescript
 * await withThemeTransition(() => {
 *   setCurrentTheme('dark')
 *   applyThemeVariables('dark')
 * })
 * ```
 */
export async function withThemeTransition(callback: () => void): Promise<void> {
  if (typeof document === 'undefined') {
    callback()
    return
  }

  // PI7: Notify listeners that transition is starting
  notifyTransitionChange(true)

  const html = document.documentElement

  try {
    // Use View Transition API if available (Chrome 111+, Edge 111+)
    if (supportsViewTransitions()) {
      const transition = (document as any).startViewTransition(() => {
        callback()
      })

      try {
        await transition.updateCallbackDone
      } catch (error) {
        // View transition was skipped or interrupted
        console.warn('View transition interrupted:', error)
      }

      return
    }

    // Fallback: opacity-based transition for other browsers
    // Add transitioning class
    html.classList.add('theme-transitioning')

    // Small delay to ensure class is applied
    await new Promise((resolve) => setTimeout(resolve, 0))

    // Execute the theme change
    callback()

    // Wait for transition to complete
    await new Promise((resolve) => setTimeout(resolve, 300))

    // Remove transitioning class
    html.classList.remove('theme-transitioning')
  } finally {
    // PI7: Notify listeners that transition is complete
    notifyTransitionChange(false)
  }
}

/**
 * Apply data attribute with smooth transition
 *
 * @param attribute - Data attribute name (e.g., 'data-theme')
 * @param value - New value for the attribute
 *
 * @example
 * ```typescript
 * await setDataAttributeWithTransition('data-theme', 'dark')
 * ```
 */
export async function setDataAttributeWithTransition(
  attribute: string,
  value: string
): Promise<void> {
  return withThemeTransition(() => {
    document.documentElement.setAttribute(attribute, value)
  })
}

/**
 * Update multiple CSS custom properties with smooth transition
 *
 * @param properties - Object of CSS custom properties to update
 *
 * @example
 * ```typescript
 * await setCSSPropertiesWithTransition({
 *   '--color-background': '#000000',
 *   '--color-text': '#ffffff',
 * })
 * ```
 */
export async function setCSSPropertiesWithTransition(
  properties: Record<string, string>
): Promise<void> {
  return withThemeTransition(() => {
    const root = document.documentElement
    Object.entries(properties).forEach(([key, value]) => {
      root.style.setProperty(key, value)
    })
  })
}

/**
 * Prefers reduced motion check
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/**
 * Get transition duration based on user preferences
 */
export function getTransitionDuration(): number {
  return prefersReducedMotion() ? 0 : 300
}
